import { useMemo, useState } from 'react';
import { parseAircraftTypeFromPhotoUrl, areSameAircraftFamily } from '../utils/aircraftTypeFromPhoto';
import { getAccessToken, isAdmin } from '../utils/auth';
import { updateAircraft } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import './DetectedAircraftType.css';

/**
 * Shown when the Planespotters photo URL names a different aircraft from the
 * one the registry gives us — or when the registry gives us nothing usable.
 * We surface what the photo says and let an admin write it to the aircraft
 * row in one click.
 *
 * Two cases:
 *  - No type, or a bare family code such as 'A330' (not an ICAO designator —
 *    the real ones are A332/A333/A339…): the photo fills the gap.
 *  - A valid type that the photo contradicts, e.g. registry BAe 146, photo a
 *    HondaJet. US registrations get recycled, so the registry is often stale
 *    while the photo (looked up by registration) shows the current airframe.
 *    This is presented as a conflict, naming both.
 *
 * Sub-variants the photo parser cannot tell apart (see areSameAircraftFamily)
 * are not treated as conflicts, so an E75L row is left alone when the photo
 * says "E175".
 *
 * Renders nothing when the photo agrees with the known type, when there is
 * no photo, or when the photo URL names a type we can't map to an ICAO
 * designator.
 *
 * @param {string} icao24      hex address the type would be saved against
 * @param {string} photoUrl    photo.imageUrl (the Planespotters photo link)
 * @param {string} knownType   current aircraft type, if any
 * @param {string} className   extra class, e.g. `detected-type-inset` when the
 *                             row sits below a panel body that carries its own
 *                             horizontal padding
 */
/**
 * @param {function} [onSaved]  called after a successful save with
 *   {icao24, aircraftType, manufacturer, model, aircraftDescription} so the
 *   caller can update its own copy of the aircraft (list rows, headers).
 */
function DetectedAircraftType({ icao24, photoUrl, knownType, className = '', onSaved = null }) {
  const detected = useMemo(() => {
    const parsed = parseAircraftTypeFromPhotoUrl(photoUrl);
    // Photo agrees with the known type (or a sub-variant of it): nothing to correct.
    if (parsed && knownType && areSameAircraftFamily(parsed.typeCode, knownType)) return null;
    return parsed;
  }, [knownType, photoUrl]);

  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);

  if (!detected) return null;

  const conflict = !!knownType;
  const rowClass = `detected-type-row${conflict ? ' detected-type-conflict' : ''}${className ? ` ${className}` : ''}`;
  const canSave = !!icao24 && isAdmin();
  const replaces = knownType ? ` (replaces ${knownType})` : '';

  const save = async () => {
    setSaveState('saving');
    setSaveError(null);
    try {
      const resp = await updateAircraft({
        icao24,
        aircraftType: detected.typeCode,
        manufacturer: detected.manufacturer,
        model: detected.model
      }, getAccessToken());

      if (isApiSuccess(resp) && resp.updated) {
        setSaveState('saved');
        if (onSaved) {
          onSaved({
            icao24,
            aircraftType: detected.typeCode,
            manufacturer: detected.manufacturer,
            model: detected.model,
            aircraftDescription: detected.label,
          });
        }
      } else {
        setSaveState('error');
        setSaveError(getFirstApiError(resp) || 'Save failed');
      }
    } catch (e) {
      setSaveState('error');
      setSaveError('Save failed');
    }
  };

  if (saveState === 'saved') {
    return (
      <div className={`${rowClass} detected-type-saved`}>
        Saved as {detected.label} ({detected.typeCode})
      </div>
    );
  }

  if (!canSave) {
    return (
      <div className={rowClass}>
        <span className="detected-type-hint">
          {conflict ? `Photo shows a different type to ${knownType}:` : 'From photo:'}
        </span>
        <span className="detected-type-label">{detected.label} ({detected.typeCode})</span>
      </div>
    );
  }

  return (
    <div className={rowClass}>
      {conflict && (
        <span className="detected-type-hint detected-type-conflict-hint">
          Type {knownType} does not match the photo
        </span>
      )}
      <button
        type="button"
        className="detected-type-btn"
        onClick={save}
        disabled={saveState === 'saving'}
        title={`Set ${icao24.toUpperCase()} to ${detected.typeCode} from the photo URL (${detected.slug})${replaces}`}
      >
        {saveState === 'saving'
          ? 'Saving…'
          : `Save as ${detected.label} (${detected.typeCode})${replaces}`}
      </button>
      {saveState === 'error' && (
        <span className="detected-type-error">{saveError}</span>
      )}
    </div>
  );
}

export default DetectedAircraftType;
